// @storybook-internal widget renderer catalogued through PlacedWidgetItem.
import React, { useState } from "react";
import { Streamdown } from "streamdown";
import "./widget-cards.css";

import type {
  T_ChatV8InsightWidgetData,
  T_ChatV8InsightWidgetMetric,
  T_ChatV8InsightWidgetRow,
} from "@lattice/core-lib/types/chat-v8";
import {
  formatChatV8AsOfLabel,
  isChatV8NarrativeInsight,
} from "@lattice/shared-frontend/chat-v8";

const MAX_COLLAPSED_SECTIONS = 3;

export type T_InsightWidgetCardProps = {
  data: T_ChatV8InsightWidgetData;
};

/** Web mirror of the mobile InsightWidgetCard (collapsed-section behavior,
 * tone-colored facts, metric chips). */
export function InsightWidgetCard({ data }: T_InsightWidgetCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hiddenSectionCount = Math.max(
    0,
    data.sections.length - MAX_COLLAPSED_SECTIONS,
  );
  const sections = expanded
    ? data.sections
    : data.sections.slice(0, MAX_COLLAPSED_SECTIONS);
  const gradedSections = data.sections.flatMap((section) =>
    section.grade && section.body
      ? [
          {
            body: section.body,
            grade: section.grade,
            title: section.title,
          },
        ]
      : [],
  );

  if (isChatV8NarrativeInsight(data)) {
    return (
      <section className="cw-narrative-section">
        <h3 className="cw-narrative-heading">Portfolio fit</h3>
        <div className="cw-narrative-body">
          <Streamdown>{data.headline}</Streamdown>
        </div>
      </section>
    );
  }

  if (gradedSections.length > 0) {
    return (
      <div className="cw-scorecard">
        {gradedSections.map((section) => (
          <section className="cw-scorecard-section" key={section.title}>
            <div className="cw-scorecard-header">
              <h3 className="cw-scorecard-title">{section.title}</h3>
              <span
                className={`cw-grade-pill cw-grade-pill--${section.grade.replace("_", "-")}`}
              >
                <span aria-hidden className="cw-grade-dot" />
                {formatAnalysisGrade(section.grade)}
              </span>
            </div>
            <div className="cw-scorecard-body">
              <Streamdown>{section.body}</Streamdown>
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="cw-card">
      <p className="cw-eyebrow">{getInsightKindLabel(data.insightKind)}</p>
      <p className="cw-title">
        {data.title}
        <span className="cw-insight-symbol">{data.symbol}</span>
      </p>
      <p className="cw-muted">
        {[data.companyName, formatChatV8AsOfLabel(data.asOf)]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="cw-insight-headline">{data.headline}</p>

      {data.metrics.length > 0 ? (
        <div className="cw-metric-grid">
          {data.metrics.map((metric) => (
            <InsightMetric key={metric.label} metric={metric} />
          ))}
        </div>
      ) : null}

      {sections.map((section) => (
        <section className="cw-insight-section" key={section.title}>
          <h4 className="cw-insight-heading">{section.title}</h4>
          {section.rows.map((row, index) => (
            <InsightFact key={`${section.title}-${index}`} row={row} />
          ))}
        </section>
      ))}

      {hiddenSectionCount > 0 ? (
        <button
          className="cw-expand"
          onClick={() => setExpanded((current) => !current)}
          type="button"
        >
          {expanded
            ? "Show fewer sections"
            : `Show ${hiddenSectionCount} more section${hiddenSectionCount === 1 ? "" : "s"}`}
        </button>
      ) : null}
    </div>
  );
}

function InsightMetric({ metric }: { metric: T_ChatV8InsightWidgetMetric }) {
  return (
    <div className="cw-metric">
      <p className="cw-metric-label">{metric.label}</p>
      <p className={`cw-metric-value ${toneClass(metric.tone)}`}>
        {metric.displayValue}
      </p>
    </div>
  );
}

function InsightFact({ row }: { row: T_ChatV8InsightWidgetRow }) {
  return (
    <p className="cw-insight-row">
      <span className={toneClass(row.tone)}>{row.label}</span>
      {row.value ? ` — ${row.value}` : null}
      {row.detail ? (
        <span className="cw-insight-metric"> {row.detail}</span>
      ) : null}
    </p>
  );
}

function formatAnalysisGrade(
  grade: NonNullable<T_ChatV8InsightWidgetData["sections"][number]["grade"]>,
): string {
  if (grade === "very_poor") return "Very poor";
  return `${grade.charAt(0).toUpperCase()}${grade.slice(1)}`;
}

function toneClass(tone: T_ChatV8InsightWidgetRow["tone"] | undefined): string {
  if (tone === "positive") return "cw-positive";
  if (tone === "negative") return "cw-negative";
  return "";
}

function getInsightKindLabel(
  insightKind: T_ChatV8InsightWidgetData["insightKind"],
): string {
  switch (insightKind) {
    case "earnings_intelligence":
      return "Earnings intelligence";
    case "insider_conviction":
      return "Insider conviction";
    case "peer_divergence":
      return "Peer divergence";
    case "thesis_evidence":
      return "Thesis evidence";
    case "portfolio_fit":
      return "Portfolio fit";
  }
}
